import React from 'react';
import { Alert, Box, Typography, useTheme } from '@mui/material';

/**
 * Shared Panel Alert Component
 * Used at the top of panels to display status/selection information
 * 
 * @param {string} severity - Alert severity: 'info', 'warning', 'error', 'success'
 * @param {string|ReactNode} primaryMessage - Main message to display
 * @param {string|ReactNode} secondaryMessage - Optional secondary/detail message
 * @param {ReactNode} action - Optional action buttons/elements
 * @param {object} sx - Additional MUI sx styles
 * @param {ReactNode} children - Alternative to primaryMessage/secondaryMessage for custom content
 */
const PanelAlert = ({ 
  severity = "info",
  primaryMessage,
  secondaryMessage,
  action,
  sx = {},
  children,
  ...props 
}) => {
  const theme = useTheme();
  
  return (
    <Alert 
      severity={severity}
      action={action}
      sx={{
        py: theme.spacing(1),
        px: theme.spacing(2),
        alignItems: 'center', // Center icon vertically
        '& .MuiAlert-icon': {
          py: 0, // Remove default padding
          alignItems: 'center'
        },
        '& .MuiAlert-message': {
          py: 0, // Remove default padding
          display: 'flex',
          alignItems: 'center'
        },
        ...sx
      }}
      {...props}
    >
      {children || (
        <Box sx={{ width: '100%' }}>
          <Typography 
            variant="body2"
            sx={{
              fontSize: theme.typography.fontSize.base,
              lineHeight: 1.4,
              fontWeight: theme.typography.fontWeight.medium
            }}
          >
            {primaryMessage}
          </Typography>
          {secondaryMessage && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                fontSize: theme.typography.fontSize.sm, 
                mt: theme.spacing(0.25), // Reduce spacing between lines
                color: 'text.secondary',
                lineHeight: 1.3
              }}
            >
              {secondaryMessage}
            </Typography>
          )}
        </Box>
      )}
    </Alert>
  );
};

export default PanelAlert;